/**
 * Settings modal.
 *
 * Reads/writes via settings.js, which handles persistence to IndexedDB.
 */

import { el, toast, openModal } from './dom.js';
import { getState } from '../state.js';
import { saveSettings } from '../settings.js';
import { clearAllData, storageEstimate } from '../lib/storage.js';
import { writeRulesBackupNow } from '../lib/rules-backup.js';
import { APP_VERSION } from '../version.js';

export function openSettings(onChange) {
  const s = { ...getState().settings };

  let themeSelect, strategySelect, thresholdInput, thresholdRow, applyModeSelect,
      dateFormatSelect, bomCheckbox, prefixInput, backupGenInput;

  const close = openModal((closeFn) => {
    const card = el('div');
    card.appendChild(el('div', { class: 'modal-head' }, [
      el('div', {
        class: 'settings-app-version',
        title: `WiseEntry build ${APP_VERSION}`
      }, [`WiseEntry v${APP_VERSION}`]),
      el('h2', {}, ['Settings'])
    ]));

    const body = el('div', { class: 'modal-body' });

    // -------- Appearance --------
    body.appendChild(el('div', { class: 'settings-section' }, [
      el('h3', {}, ['Appearance']),
      el('div', { class: 'section-help' }, [
        'Use Light, OLED-style Dark, or follow your device. Theme saves automatically when you change this menu — other sections use Save.'
      ]),
      (() => {
        themeSelect = el('select', {
          onChange: async () => {
            const v = themeSelect.value;
            const themePreference =
              v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
            try {
              await saveSettings({ themePreference });
            } catch (err) {
              toast(
                String(err?.message || err || 'Could not save theme.'),
                'error',
                5000
              );
              console.error(err);
            }
          }
        }, [
          el('option', { value: 'light' }, ['Light']),
          el('option', { value: 'dark' }, ['Dark']),
          el('option', { value: 'system' }, ['Match system'])
        ]);
        const tp = s.themePreference;
        themeSelect.value =
          tp === 'light' || tp === 'dark' || tp === 'system' ? tp : 'system';
        return el('div', { class: 'settings-row' }, [
          el('label', {}, ['Theme']),
          el('div', {}, [themeSelect])
        ]);
      })()
    ]));

    // -------- Vendor matching --------
    body.appendChild(el('div', { class: 'settings-section' }, [
      el('h3', {}, ['Vendor matching']),
      el('div', { class: 'section-help' }, [
        'How rules are matched against extracted vendor names. Affects automatic coding.'
      ]),
      (() => {
        strategySelect = el('select', {
          onChange: () => {
            const isFuzzy = strategySelect.value === 'fuzzy';
            thresholdRow.style.display = isFuzzy ? '' : 'none';
          }
        }, [
          el('option', { value: 'exact' }, ['Exact (case-sensitive equality)']),
          el('option', { value: 'normalized' }, ['Normalized (case-insensitive, ignores LLC/Inc/etc.)']),
          el('option', { value: 'fuzzy' }, ['Fuzzy (similarity threshold)'])
        ]);
        strategySelect.value = s.matchStrategy || 'normalized';
        return el('div', { class: 'settings-row' }, [
          el('label', {}, ['Match strategy']),
          el('div', {}, [strategySelect])
        ]);
      })(),
      (() => {
        thresholdInput = el('input', {
          type: 'range', min: '0.6', max: '1', step: '0.01',
          value: String(s.matchThreshold || 0.85),
          oninput: () => { thresholdLabel.textContent = thresholdInput.value; }
        });
        const thresholdLabel = el('span', {
          style: { marginLeft: '12px', fontFamily: 'var(--font-mono)' }
        }, [String(s.matchThreshold || 0.85)]);
        thresholdRow = el('div', {
          class: 'settings-row',
          style: { display: s.matchStrategy === 'fuzzy' ? '' : 'none' }
        }, [
          el('label', {}, ['Threshold']),
          el('div', { style: { display: 'flex', alignItems: 'center' } }, [
            thresholdInput, thresholdLabel,
            el('div', { class: 'help', style: { marginLeft: '12px' } }, ['Higher = stricter'])
          ])
        ]);
        return thresholdRow;
      })(),
      (() => {
        applyModeSelect = el('select', {}, [
          el('option', { value: 'auto' }, ['Auto (apply matched rules immediately)']),
          el('option', { value: 'suggest' }, ['Suggest (require manual confirmation)'])
        ]);
        applyModeSelect.value = s.applyMode || 'auto';
        return el('div', { class: 'settings-row' }, [
          el('label', {}, ['Apply mode']),
          el('div', {}, [applyModeSelect])
        ]);
      })()
    ]));

    // -------- CSV --------
    body.appendChild(el('div', { class: 'settings-section' }, [
      el('h3', {}, ['CSV output']),
      el('div', { class: 'section-help' }, [
        'How dates and the file itself are formatted on export.'
      ]),
      (() => {
        dateFormatSelect = el('select', {}, [
          el('option', { value: 'YYYY-MM-DD' }, ['YYYY-MM-DD (ISO)']),
          el('option', { value: 'MM/DD/YYYY' }, ['MM/DD/YYYY (US)']),
          el('option', { value: 'DD/MM/YYYY' }, ['DD/MM/YYYY (EU)'])
        ]);
        dateFormatSelect.value = s.dateFormat || 'YYYY-MM-DD';
        return el('div', { class: 'settings-row' }, [
          el('label', {}, ['Date format']),
          el('div', {}, [dateFormatSelect])
        ]);
      })(),
      (() => {
        prefixInput = el('input', {
          type: 'text', class: 'input-mono',
          value: s.csvFilenamePrefix || 'invoices'
        });
        return el('div', { class: 'settings-row' }, [
          el('label', {}, ['Filename prefix']),
          el('div', {}, [
            prefixInput,
            el('div', { class: 'help' }, ['Becomes part of the auto-generated CSV filename.'])
          ])
        ]);
      })(),
      (() => {
        bomCheckbox = el('input', { type: 'checkbox', id: 'csv-bom', checked: !!s.csvBom });
        return el('div', { class: 'settings-row' }, [
          el('label', { for: 'csv-bom' }, ['BOM']),
          el('div', {}, [
            el('label', { for: 'csv-bom', style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' } }, [
              bomCheckbox,
              'Prepend UTF-8 BOM to the CSV (helps Excel open Unicode names correctly)'
            ])
          ])
        ]);
      })()
    ]));

    // -------- Rules backups (app-config/backups/) --------
    body.appendChild(el('div', { class: 'settings-section' }, [
      el('h3', {}, ['Rules backups']),
      el('div', { class: 'section-help' }, [
        'When an app-config folder is connected, rule changes are copied to ',
        el('code', {}, ['backups/rules-*.json']),
        ' (and pre-migration snapshots when the database version changes). Keep generations between 1 and 20.'
      ]),
      (() => {
        const n = Math.min(20, Math.max(1, Number(s.backupRuleGenerations) || 5));
        backupGenInput = el('input', {
          type: 'number', min: '1', max: '20', step: '1',
          class: 'input-mono',
          style: { width: '80px' },
          value: String(n)
        });
        return el('div', { class: 'settings-row' }, [
          el('label', {}, ['Generations to keep']),
          el('div', {}, [
            backupGenInput,
            el('div', { class: 'help', style: { marginTop: '6px' } }, [
              'Oldest files are removed when a new backup is written.'
            ])
          ])
        ]);
      })(),
      el('div', { style: { marginTop: '10px' } }, [
        el('button', {
          type: 'button',
          class: 'ghost-btn',
          onClick: async () => {
            try {
              await writeRulesBackupNow();
              toast('Rules backup written under app-config/backups/.', 'success');
            } catch (err) {
              toast(err.message || 'Backup failed. Connect app-config folder with write access.', 'error', 5000);
            }
          }
        }, ['Back up rules now'])
      ])
    ]));

    // -------- Model (read-only) --------
    body.appendChild(el('div', { class: 'settings-section' }, [
      el('h3', {}, ['Model']),
      el('div', { class: 'settings-row' }, [
        el('label', {}, ['Anthropic model']),
        el('div', {}, [
          el('span', {
            class: 'coding-pill',
            style: { fontSize: '13px' }
          }, [s.model || 'claude-sonnet-4-6']),
          el('div', { class: 'help' }, ['Sonnet 4.6 is currently the only supported model. ~$0.01–0.03 per invoice depending on length.'])
        ])
      ])
    ]));

    // -------- Storage / danger zone --------
    body.appendChild(el('div', { class: 'settings-section' }, [
      el('h3', {}, ['Storage']),
      el('div', { class: 'section-help', id: 'storage-help' }, ['Loading…']),
      el('div', { style: { marginTop: '10px' } }, [
        el('button', {
          type: 'button',
          class: 'danger-btn',
          onClick: async () => {
            if (!confirm(
              'Clear ALL WiseEntry data on this device?\n\n' +
              'This deletes: encrypted API key, vendor rules, batch snapshots, settings, and remembered folder handles.\n\n' +
              'Files in your work folders are NOT touched.'
            )) return;
            await clearAllData();
            toast('All WiseEntry data cleared. Reloading…', 'info', 2000);
            setTimeout(() => location.reload(), 1500);
          }
        }, ['Clear all data on this device'])
      ])
    ]));

    card.appendChild(body);

    card.appendChild(el('div', { class: 'modal-foot' }, [
      el('button', {
        type: 'button',
        class: 'primary-btn',
        onClick: async () => {
          const gen = Math.min(20, Math.max(1, parseInt(backupGenInput.value, 10) || 5));
          const pref = themeSelect.value;
          const themePreference =
            pref === 'light' || pref === 'dark' || pref === 'system' ? pref : 'system';
          const next = {
            matchStrategy:    strategySelect.value,
            matchThreshold:   parseFloat(thresholdInput.value),
            applyMode:        applyModeSelect.value,
            dateFormat:       dateFormatSelect.value,
            csvBom:           bomCheckbox.checked,
            csvFilenamePrefix: prefixInput.value.trim() || 'invoices',
            backupRuleGenerations: gen,
            themePreference
          };
          try {
            await saveSettings(next);
          } catch (err) {
            toast(
              String(err?.message || err || 'Could not save settings. Check IndexedDB/storage permissions.'),
              'error',
              6500
            );
            console.error(err);
            return;
          }
          toast('Settings saved.', 'success');
          closeFn();
          if (onChange) onChange();
        }
      }, ['Save']),
      el('button', { type: 'button', class: 'skip-link', onClick: closeFn }, ['Cancel'])
    ]));

    // Storage estimate (async)
    storageEstimate().then(est => {
      const help = card.querySelector('#storage-help');
      if (!help) return;
      if (!est) { help.textContent = 'Storage usage unavailable in this browser.'; return; }
      const usedMb = (est.usage / 1048576).toFixed(1);
      const quotaMb = est.quota ? (est.quota / 1048576).toFixed(0) : '?';
      help.textContent = `Using ${usedMb} MB of ${quotaMb} MB available browser storage.`;
    });

    return card;
  }, { large: true });

  return close;
}
